/**
 * Availability profile resolver registry.
 *
 * Mirrors `packages/contracts/src/import/adapters/registry.ts`: an ordered
 * `AVAILABILITY_RESOLVERS` array and a `resolveAvailabilityProfile` function
 * that calls each resolver in array order and returns the first result
 * whose confidence clears the threshold, or the highest-confidence result
 * of all resolvers if none clears it. This change registers exactly two
 * deterministic resolvers plus the generic fallback scorer, which always
 * returns a non-null result — so `resolveAvailabilityProfile` never returns
 * `null`. A tier-3 AI resolver (CU-86ca9wakq) is appended to this same
 * array in a later change without changing call sites.
 *
 * Traceability: availability-profile-resolution / Requirement:
 * AvailabilityProfileResolver interface and ordered resolution.
 */
import type { AvailabilityProfileResolver, AvailabilityProfileResolveResult } from './types.js';
import { omiResolver } from './omi-resolver.js';
import { monthMatrixResolver } from './month-matrix-resolver.js';
import { genericResolver } from './generic-resolver.js';

/** Minimum confidence a resolver's result must clear to win outright. Also
 * reused by web preview UI to flag a low-confidence resolution for operator
 * review — see `preview-phase.tsx`'s `CONFIDENT_THRESHOLD`. */
export const CONFIDENCE_THRESHOLD = 0.7;

export const AVAILABILITY_RESOLVERS: readonly AvailabilityProfileResolver[] = [
  omiResolver,
  monthMatrixResolver,
  genericResolver,
] as const;

/**
 * Resolves the `AvailabilityProfile` for a file's headers and a sample of
 * its data rows. Calls each resolver in `AVAILABILITY_RESOLVERS` order and
 * returns the first result whose confidence clears `0.7`; if none clears
 * it, returns the highest-confidence result of all resolvers. Never
 * returns `null` — the generic fallback scorer guarantees a non-null tail.
 */
export function resolveAvailabilityProfile(
  headers: string[],
  sampleRows: Record<string, string>[],
): AvailabilityProfileResolveResult {
  let best: AvailabilityProfileResolveResult | null = null;

  for (const resolver of AVAILABILITY_RESOLVERS) {
    const result = resolver.resolve(headers, sampleRows);
    if (result === null) continue;
    if (result.confidence >= CONFIDENCE_THRESHOLD) return result;
    if (best === null || result.confidence > best.confidence) best = result;
  }

  if (best === null) {
    // Unreachable in practice: the generic fallback resolver never returns
    // null, so at least one iteration always sets `best`. Guarded here so
    // the function's return type stays non-nullable without an unsound
    // non-null assertion.
    throw new Error(
      'resolveAvailabilityProfile: no resolver produced a result — the generic fallback must always return non-null',
    );
  }

  return best;
}
